import { FunctionComponent, useCallback, useMemo, MouseEvent } from 'react';
import { UrlObject } from '../../hooks/useRouterState';
import ButtonLink from '../ButtonLink';
import styles from './styles.module.scss';

export type PaginationLinksProps = {
    currentPage: number;
    setPage: (page: number) => void;
    getPageLink: (page: number) => UrlObject;
    totalPages: number;
};

const PaginationLinks: FunctionComponent<PaginationLinksProps> = ({
    currentPage,
    setPage,
    getPageLink,
    totalPages,
}) => {
    const getClassName = useCallback(
        (pageNumber: number) => {
            return pageNumber === currentPage ? styles.currentPage : '';
        },
        [currentPage]
    );

    const getLinkProps = useCallback(
        (page: number) => {
            return {
                onClick: (event: MouseEvent) => {
                    event.preventDefault();
                    setPage(page);
                    window.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth',
                    });
                },
                href: getPageLink(page),
            };
        },
        [setPage, getPageLink]
    );

    /**
     * Faux pagination links provides the user a way back to
     * the first page of results when we don't know how
     * many pages there are.
     */
    const isFauxPagination = useMemo<boolean>(() => {
        if (currentPage > 1 && totalPages === 0) {
            return true;
        }

        return false;
    }, [currentPage, totalPages]);

    const links = useMemo(() => {
        const links = [];

        // Always show the first page
        links.push(
            <ButtonLink {...getLinkProps(1)} className={getClassName(1)} key="page-1">
                1
            </ButtonLink>
        );

        // Add ellipsis if the current page is more than 4 away from the first page
        if (currentPage > 4 || isFauxPagination) {
            links.push(
                <span className={styles.ellipsis} key="ellipsis1">
                    ...
                </span>
            );
        }

        // Add the 2 pages before and after the current page
        if (!isFauxPagination) {
            for (let i = 0; i < 5; i++) {
                const pageNumber = currentPage - 2 + i;
                if (pageNumber < 2 || pageNumber > totalPages - 1) {
                    // Skip pages that are the first or last page
                    continue;
                }

                links.push(
                    <ButtonLink
                        {...getLinkProps(pageNumber)}
                        className={getClassName(pageNumber)}
                        key={`page-${pageNumber}`}
                        scroll={false}
                    >
                        {pageNumber}
                    </ButtonLink>
                );
            }
        } else {
            links.push(
                <ButtonLink
                    {...getLinkProps(currentPage)}
                    className={getClassName(currentPage)}
                    key={`page-${currentPage}`}
                    scroll={false}
                >
                    {currentPage}
                </ButtonLink>
            );
        }

        // Add ellipsis if the current page is more than 4 away from the last page
        if (!isFauxPagination && currentPage + 2 < totalPages - 1) {
            links.push(
                <span className={styles.ellipsis} key="ellipsis2">
                    ...
                </span>
            );
        }

        if (!isFauxPagination && totalPages > 1) {
            links.push(
                <ButtonLink
                    {...getLinkProps(totalPages)}
                    className={getClassName(totalPages)}
                    key={`page-${totalPages}`}
                >
                    {totalPages}
                </ButtonLink>
            );
        }

        return links;
    }, [currentPage, getClassName, totalPages, getLinkProps, isFauxPagination]);

    return <div className={styles.pagination}>{links}</div>;
};

export default PaginationLinks;
